import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { FeatureResponse, TaskResponse } from '../client';
import { useSimulationContext } from '../SimulationContext';

interface Navigation {
  toFeature: (feature: FeatureResponse) => void;
  toFeatureById: (featureId: string) => void;
  toTask: (task: TaskResponse) => void;
  toAccumulatedValueChart: () => void;
  toDashboard: () => void;
  toJoinQR: () => void;
  toHome: () => void;
  back: () => void;
}

/**
 * Navigation helper that builds URLs using the current simulation ID from context.
 * Must be used within a layout that provides SimulationContext.
 *
 * For participant pages, also needs teamName — callers should use the specific
 * participant/operator methods as appropriate for their route tree.
 */
export const useAppNavigate = (): Navigation => {
  const navigate = useNavigate();
  const { simId } = useSimulationContext();

  return useMemo(() => {
    return {
      toFeature: (feature: FeatureResponse) => {
        const teamName = getTeamNameFromPath();
        navigate(`/sim/${simId}/${teamName}/features/${feature.id}`);
      },
      toFeatureById: (featureId: string) => {
        const teamName = getTeamNameFromPath();
        navigate(`/sim/${simId}/${teamName}/features/${featureId}`);
      },
      toTask: (task: TaskResponse) => {
        const teamName = getTeamNameFromPath();
        navigate(`/sim/${simId}/${teamName}/tasks/${task.id}`);
      },
      toAccumulatedValueChart: () => {
        navigate(`/operator/${simId}/value`);
      },
      toDashboard: () => {
        navigate(`/operator/${simId}/dashboard`);
      },
      toJoinQR: () => {
        navigate(`/operator/${simId}/qr`);
      },
      toHome: () => {
        navigate('/');
      },
      back: () => {
        navigate(-1);
      },
    };
  }, [navigate, simId]);
};

/** Extract the teamName segment from the current URL path (/sim/:simId/:teamName/...) */
function getTeamNameFromPath(): string {
  const parts = window.location.pathname.split('/');
  // /sim/:simId/:teamName/... → parts = ['', 'sim', simId, teamName, ...]
  if (parts.length >= 4 && parts[1] === 'sim') {
    return parts[3];
  }
  throw new Error('Cannot determine team name from URL — are you in a participant route?');
}
